import { ParamsTimeType } from '@op/types/common';
import {
  arrayToSimpleObj,
  dateTypeMap,
  getPercentageValue,
  getRationPercentValue,
  getTimeSpaceArray,
  sortTimeListFunc,
  uniqueArray,
} from '@op/utils';
import { getDefaultValue } from '@op/utils/value';
import moment from 'moment';

type CompetitorStateListValueType = {
  account_posts: number;
  comments: number;
  game_store_reviews: number;
  mentions: number;
  video: number;
};
export type CompetitorStateListType = Array<{
  edition_unified_id: string;
  list: CompetitorStateListValueType;
  old_list: CompetitorStateListValueType;
  new_start_date: string;
  new_end_date: string;
  old_start_date: string;
  old_end_date: string;
}>;

type CompetitorStateTimeType = Array<{
  edition_unified_id: string;
  timeline_of_start_time: string;
  timeline_of_end_time: string;
  timeline: {
    time: string;
    value: number;
  }[];
}>;

export function dealwithMentionsSentimentTable(
  data: CompetitorStateListType,
  _filter: any,
) {
  const { sort } = _filter || {};
  const total: Record<string, number> = {};
  const records = (data || []).map(item => {
    const {
      edition_unified_id,
      list,
      old_list,
      new_start_date,
      new_end_date,
      old_start_date,
      old_end_date,
    } = item || {};
    const newList: any = list || {};
    const oldList: any = old_list || {};
    const newKeys = Object.keys(newList);
    const oldKeys = Object.keys(oldList);
    const keys = uniqueArray([...newKeys, ...oldKeys]);
    const valueMap = keys.reduce((pre, key) => {
      const oldKey = `${key}_old`;
      const ratioKey = `${key}_ratio`;
      const value = newList[key] || 0;
      const oldValue = oldList[key] || 0;
      const ratio = getRationPercentValue(value, oldValue, 6);
      total[`${key}_total`] = (total[`${key}_total`] || 0) + value;
      return { ...pre, [key]: value, [oldKey]: oldValue, [ratioKey]: ratio };
    }, {});

    return {
      ...valueMap,
      game: edition_unified_id,
      edition_unified_id,
      newTime: [new_start_date, new_end_date],
      oldTime: [old_start_date, old_end_date],
    };
  });

  const recordsWithTotal = records.map(val => ({
    ...val,
    ...total,
  }));

  return recordsWithTotal.sort(
    sortTimeListFunc(sort?.by || 'game', sort?.order || 'asc'),
  );
}

function dealwithSteamValue(list: { [key: string]: number }, tab: string) {
  const positiveKey = `${tab}_recommend`;
  const negativeKey = `${tab}_non_recommend`;
  let score = list?.score || 0;
  let negativeValue = list?.[negativeKey] || 0;
  let totalValue = list?.[tab] || 0;

  if (tab === 'added_reviews') {
    const tabKey = 'select_reviews';
    const newPositiveKey = `${tabKey}_recommend`;
    const newNegativeKey = `${tabKey}_non_recommend`;
    const positiveValue = list?.[newPositiveKey] || 0;
    negativeValue = list?.[newNegativeKey] || 0;
    totalValue = list?.[tabKey] || 0;
    score = getPercentageValue(positiveValue, totalValue, 6);
  }
  return {
    ...list,
    [tab]: totalValue,
    [positiveKey]: score,
    [negativeKey]: negativeValue === 0 ? 0 : 1 - score,
  };
}
export function dealWithSteamTable(data: any, filter: any) {
  const by_type = filter?.by_type || 'all_reviews';
  const newData = data.map((item: { [x: string]: any }) => {
    return {
      ...item,
      list: dealwithSteamValue(item.list, by_type),
      old_list: dealwithSteamValue(item.old_list, by_type),
    };
  });
  return dealwithMentionsSentimentTable(newData, filter);
}

function dealwithMobileGameStoreValue(list: { [key: string]: number }) {
  const { negative, positive, mentions } = list || {};
  return {
    ...list,
    negative: getPercentageValue(negative, mentions, 6),
    positive: getPercentageValue(positive, mentions, 6),
  };
}
export function dealWithMobileGameStoreTable(data: any, filter: any) {
  const newData = data.map((item: { [x: string]: any }) => {
    return {
      ...item,
      list: dealwithMobileGameStoreValue(item.list),
      old_list: dealwithMobileGameStoreValue(item.old_list),
    };
  });
  return dealwithMentionsSentimentTable(newData, filter);
}

export function dealWithMentionsSentimentChart(
  data: CompetitorStateTimeType,
  _filter?: any,
) {
  const tableData: {
    time: string;
    name: string;
    value: number;
    ratio: number;
  }[] = [];
  const { sort, date_type, gameData, by_type } = _filter || {};
  const getGameData = getGameColorOrName(gameData);

  let timeList: Array<string> = [];
  const records =
    data?.map(val => {
      const {
        edition_unified_id,
        timeline,
        timeline_of_start_time,
        timeline_of_end_time,
      } = val;
      const dateType = dateTypeMap[date_type as ParamsTimeType];
      const format = dateType === 'hour' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';
      if (timeList.length === 0) {
        timeList = getTimeSpaceArray(
          timeline_of_start_time,
          timeline_of_end_time,
          dateType,
          format,
        );
      }
      const valueMap = arrayToSimpleObj(timeline, 'value', item =>
        moment(item.time).format(format),
      );

      const data = timeList.map((time, index) => {
        const value = getDefaultValue(valueMap[time], by_type);
        // 这个key有问题，因为index为0的时候，前面是没有值的
        let oldKey = timeList?.[index - 1];
        if (index === 0) {
          oldKey = moment(time).subtract(1, dateType).format(format);
        }
        const oldValue = getDefaultValue(valueMap[oldKey], by_type);
        const ratio = getRationPercentValue(value, oldValue, 6);
        tableData.push({
          time,
          name: getGameData?.(edition_unified_id) || edition_unified_id,
          value,
          ratio,
        });
        return value;
      });
      return {
        name: getGameData?.(edition_unified_id) || edition_unified_id,
        data,
      };
    }) || [];

  return {
    xAxisData: timeList,
    yAxisData: records,
    tableData: tableData.sort(
      sortTimeListFunc(sort?.by || 'time', sort?.order || 'desc'), // TO DO 排序
    ),
  };
}

export const getGameColorOrName = (data: any) => {
  return (
    id: string,
    key: 'color' | 'cover' | 'entity_name' | 'all' = 'entity_name',
  ): string | any => {
    const item = data?.[id];

    if (key === 'all') return item;

    return item?.[key] || id;
  };
};
